import { Link } from 'react-router-dom'

import type { PeriodInfo, Trend, TrendDirection } from '@/api/types'
import { Card, EmptyState, Icon, LoadingBlock, Sparkline, type IconName } from '@/design'
import { usePageFreshness } from '@/lib/freshness'

import { CATEGORY_LABELS } from '@/lib/insights/categoryLabels'
import { formatFactPeriod } from '@/lib/insights/factFormat'
import { useInsightsRead } from '@/lib/insights/queries'

import { useDrillDown } from './hooks'
import './insights.css'

const DIRECTION: Record<TrendDirection, { icon: IconName | null; tone: 'positive' | 'negative' | 'neutral' }> = {
  up: { icon: 'arrowUp', tone: 'positive' },
  down: { icon: 'arrowDown', tone: 'negative' },
  flat: { icon: null, tone: 'neutral' },
}

/** A trend without a direction: either week could not be measured. */
const NO_DIRECTION = DIRECTION.flat

function TrendCard({ trend }: { trend: Trend }) {
  const href = useDrillDown()
  const { icon, tone } = trend.direction ? DIRECTION[trend.direction] : NO_DIRECTION

  return (
    <Card
      className="in-trend"
      title={trend.title}
      subtitle={CATEGORY_LABELS[trend.category]}
      footer={
        <Link className="in-footer-link" to={href(trend.drillDown)}>
          Open in analytics <Icon name="arrowRight" size={14} />
        </Link>
      }
    >
      {trend.changeLabel ? (
        <p className="in-trend__change" data-tone={tone}>
          {icon ? <Icon name={icon} size={14} /> : null}
          {trend.changeLabel}
        </p>
      ) : (
        <p className="in-trend__change in-muted">Not available</p>
      )}
      <p className="in-trend__explanation">{trend.explanation}</p>
      <Sparkline values={trend.sparkline} tone={tone} />
    </Card>
  )
}

/** "Mon 28 Sep 2026 to Sun 4 Oct 2026, against the week before", from the API's own week. */
function weekLabel(period: PeriodInfo | undefined): string | null {
  if (!period) return null
  const week = formatFactPeriod(period.from, period.to)
  return period.compareLabel ? `${week}, against ${period.compareLabel}` : week
}

/**
 * Insights > Trends: movements over eight weeks rather than a single day, so a
 * one-off spike does not read as a trend. Every change, label and sparkline
 * point is the API's, shown as given; a week it could not measure is a gap,
 * never a zero. Each card links to the analytics page behind it.
 */
export function TrendsPage() {
  const trends = useInsightsRead('trends', (client) => client.trends())
  usePageFreshness(trends.data?.meta)
  const items = trends.data?.data ?? []

  if (trends.isPending) return <LoadingBlock rows={6} label="Loading trends" />
  if (trends.isError) {
    return (
      <Card>
        <EmptyState variant="outage" description="Trends could not be loaded." />
      </Card>
    )
  }
  if (items.length === 0) {
    return (
      <Card>
        <EmptyState variant="no-results" title="No trends yet" description="Longer-running movements appear here once there is enough history." />
      </Card>
    )
  }

  const week = weekLabel(trends.data.meta.period)
  return (
    <div className="in-stack">
      {week ? <p className="in-muted in-trend-period">{week}</p> : null}
      <div className="in-trend-grid" aria-label="Trends">
        {items.map((trend) => (
          <TrendCard key={trend.id} trend={trend} />
        ))}
      </div>
    </div>
  )
}
